import React from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, IconButton, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import PlaceIcon from "@mui/icons-material/Place";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import WrongLocationIcon from "@mui/icons-material/WrongLocation";
import { QRCodeSVG } from "qrcode.react";
import {
  createCheckinCode, createPrintedCheckinCode, fetchCheckinReview, resolveCheckinReview, saveCheckinSettings,
} from "../../../api/backend";
import { getPosition } from "../../checkin/getPosition";
import CheckinReviewPanel from "./CheckinReviewPanel";
import CheckinSettingsPanel from "./CheckinSettingsPanel";
import PrintedCodeView from "./PrintedCodeView";

/** A little after the server rotates, so the refresh lands in the new window rather than racing the old one. */
const REFRESH_PADDING_MS = 500;
const MIN_REFRESH_MS = 5000;
const TALLY_POLL_MS = 10000;

const messageOf = (err, fallback) => err?.response?.data?.message || fallback;

/**
 * Check-in control for one session: the rotating code (it changes every window, so a photo of the screen goes stale),
 * the session's check-in options, a printable code, and the check-ins waiting for the instructor's review. "Use my
 * location" saves where the instructor is standing as the room's location, which later scans are compared against.
 */
export default function CheckinQrDialog({ open, session, onClose, acknowledgeOffHours = false }) {
  const [code, setCode] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [locating, setLocating] = React.useState(false);
  const [locationNote, setLocationNote] = React.useState(null);
  const [review, setReview] = React.useState([]);
  const [tally, setTally] = React.useState(null);
  const [reviewError, setReviewError] = React.useState(null);
  const [busyId, setBusyId] = React.useState(null);
  const [printed, setPrinted] = React.useState(null);
  const [printError, setPrintError] = React.useState(null);
  const [expiresAt, setExpiresAt] = React.useState(null);
  const [now, setNow] = React.useState(() => Date.now());
  const [copied, setCopied] = React.useState(false);
  const qrCard = React.useRef(null);
  const timer = React.useRef(null);
  const alive = React.useRef(false);
  const requestSeq = React.useRef(0);
  // The instructor's off-hours acknowledgement is sent until one request succeeds; the server then remembers it, so the
  // rotating refreshes carry nothing extra (and do not write another audit record).
  const ackPending = React.useRef(false);

  // The timer must call the latest load, not the one from the render that scheduled it: the dialog is mounted closed
  // (no session yet), so a captured first-render load would refresh against a null session and fail forever.
  const loadRef = React.useRef(null);

  const schedule = React.useCallback((ms) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => loadRef.current?.(), ms);
  }, []);

  const loadReview = React.useCallback(async () => {
    try {
      const data = await fetchCheckinReview(session.id);
      if (alive.current) {
        setReview(data?.items ?? []);
        setTally(typeof data?.checkedInCount === "number" ? { checkedIn: data.checkedInCount, enrolled: data.enrolledCount ?? 0 } : null);
      }
    } catch {
      // The code matters more than the review list; a failed refresh just leaves the last list showing.
    }
  }, [session?.id]);

  const load = React.useCallback(async (position) => {
    const seq = ++requestSeq.current;
    try {
      const next = ackPending.current
        ? await createCheckinCode(session.id, position ?? {}, { acknowledgeOffHours: true })
        : await createCheckinCode(session.id, position ?? {});
      if (!alive.current || seq !== requestSeq.current) return null;
      ackPending.current = false;
      setCode(next);
      setError(null);
      setNow(Date.now());
      setExpiresAt(Date.now() + (next.expiresInSeconds ?? next.windowSeconds ?? 60) * 1000);
      schedule(Math.max((next.expiresInSeconds ?? next.windowSeconds ?? 60) * 1000 + REFRESH_PADDING_MS, MIN_REFRESH_MS));
      loadReview();
      return next;
    } catch (err) {
      if (!alive.current || seq !== requestSeq.current) return null;
      setCode(null);
      setError(messageOf(err, "Couldn't load the check-in code. Try again."));
      // A blip must not leave the screen dead: keep trying until it recovers or the dialog closes.
      schedule(MIN_REFRESH_MS);
      return null;
    }
  }, [session?.id, schedule, loadReview]);

  loadRef.current = load;

  React.useEffect(() => {
    if (!open || !session) return undefined;
    alive.current = true;
    ackPending.current = acknowledgeOffHours;
    setCode(null);
    setError(null);
    setLocationNote(null);
    setReview([]);
    setTally(null);
    setReviewError(null);
    setPrinted(null);
    setPrintError(null);
    load();
    return () => {
      alive.current = false;
      clearTimeout(timer.current);
    };
  }, [open, session, load, acknowledgeOffHours]);

  // Students check in throughout the window, so keep the tally and review list current between code refreshes.
  React.useEffect(() => {
    if (!open || !session || !code || printed) return undefined;
    const poll = setInterval(() => loadReview(), TALLY_POLL_MS);
    return () => clearInterval(poll);
  }, [open, session, code, printed, loadReview]);

  // Drives the "new code in Ns" countdown; only runs while a live code is on screen.
  React.useEffect(() => {
    if (!open || !code || printed) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [open, code, printed]);

  const useMyLocation = async () => {
    setLocating(true);
    setLocationNote(null);
    const position = await getPosition();
    if (!position) {
      if (alive.current) {
        setLocationNote({ kind: "error", text: "Couldn't get your location. Check that location access is allowed for this site." });
        setLocating(false);
      }
      return;
    }
    const next = await load(position);
    if (alive.current) {
      if (next) setLocationNote({ kind: "ok", text: "Room location saved." });
      setLocating(false);
    }
  };

  const saveSettings = async (settings) => {
    const saved = await saveCheckinSettings(session.id, settings);
    if (alive.current) setCode((prev) => (prev ? { ...prev, settings: { ...prev.settings, ...saved } } : prev));
  };

  const resolve = async (item, action) => {
    setBusyId(item.attendanceId);
    setReviewError(null);
    try {
      await resolveCheckinReview(session.id, item.attendanceId, action);
      await loadReview();
    } catch (err) {
      setReviewError(messageOf(err, "Couldn't save that review. Try again."));
    } finally {
      if (alive.current) setBusyId(null);
    }
  };

  const showPrinted = async () => {
    setPrintError(null);
    try {
      const data = await createPrintedCheckinCode(session.id, {});
      if (!alive.current) return;
      // The printed view has its own code; stop the live one rotating behind it until the instructor comes back,
      // including any refresh still in flight (its answer would otherwise restart the rotation).
      requestSeq.current += 1;
      clearTimeout(timer.current);
      setPrinted({ url: `${window.location.origin}${data.checkinPath}`, session: { ...session, ...data.session } });
      setCode((prev) => (prev ? { ...prev, settings: { ...prev.settings, printIssued: true } } : prev));
    } catch (err) {
      if (alive.current) setPrintError(messageOf(err, "Couldn't make a printable code. Try again."));
    }
  };

  const backToLive = () => {
    setPrinted(null);
    load();
  };

  // Stable between renders, so the settings panel does not reset its in-flight edits every time the dialog redraws.
  const settings = React.useMemo(
    () => (code ? { ...code.settings, locationSet: code.locationSet } : null),
    [code],
  );

  const copyLink = async () => {
    try {
      await navigator.clipboard?.writeText(url);
      setCopied(true);
      setTimeout(() => { if (alive.current) setCopied(false); }, 1600);
    } catch {
      // Clipboard access can be refused; the link stays visible to copy by hand.
    }
  };

  const fullScreen = () => qrCard.current?.requestFullscreen?.().catch(() => {});

  if (!open || !session) return null;

  const url = code ? `${window.location.origin}${code.checkinPath}` : "";
  const windowSeconds = code?.windowSeconds ?? 60;
  const secsLeft = Math.max(0, Math.min(windowSeconds, Math.ceil(((expiresAt ?? now) - now) / 1000)));
  const when = [session.date, session.startTime && session.endTime ? `${session.startTime} – ${session.endTime}` : null]
    .filter(Boolean).join(" · ");

  const darkBtn = {
    textTransform: "uppercase", fontWeight: 600, fontSize: 12.5, height: 40, color: "#fff", gap: 1,
    bgcolor: "var(--color-on-dark-faint)", "&:hover": { bgcolor: "rgba(255,255,255,.26)" }, "&.Mui-disabled": { color: "rgba(255,255,255,.4)" },
  };

  const printedView = <PrintedCodeView url={printed?.url} session={printed?.session} onBack={backToLive} />;

  return (
    <Dialog
      open onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ "aria-label": "Check-in code", sx: { borderRadius: "16px", overflow: "hidden" } }}
    >
      {printed ? (
        <>
          <DialogContent>{printedView}</DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" onClick={onClose} sx={{ textTransform: "none" }}>Close</Button>
          </DialogActions>
        </>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0,400px) minmax(0,1fr)" }, minHeight: 0, maxHeight: { md: "calc(100vh - 64px)" } }}>
          <Box component="section" sx={{ bgcolor: "var(--color-surface-canvas-dark)", color: "#fff", p: 3.5, display: "flex", flexDirection: "column", gap: 2.5, overflow: "auto" }}>
            <Box>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, fontSize: 11, fontWeight: 600, letterSpacing: ".12em", textTransform: "uppercase", color: "var(--color-accent-lime)" }}>
                <Box component="i" sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "var(--color-accent-lime)" }} />
                Check-in code
              </Box>
              <Typography component="h2" sx={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 26, fontWeight: 700, letterSpacing: "-.02em", lineHeight: 1.1, mt: 0.75, mb: 0.5 }}>
                {session.name}
              </Typography>
              <Typography sx={{ color: "var(--color-on-dark-muted)", fontSize: 13 }}>
                {[when, session.location].filter(Boolean).join(" · ")}
              </Typography>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}
            {!code && !error && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress aria-label="Loading code" /></Box>
            )}

            {code && (
              <Box ref={qrCard} sx={{ bgcolor: "#fff", color: "var(--color-ink)", borderRadius: "12px", p: 2.25, display: "grid", gap: 1.75 }}>
                <Box data-testid="checkin-qr" sx={{ display: "grid", placeItems: "center", "& svg": { width: "100%", height: "auto", maxHeight: "44vh" } }}>
                  <QRCodeSVG value={url} size={240} level="M" includeMargin={false} />
                </Box>
                <Box sx={{ display: "grid", gap: 0.75 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#4a4257" }}>
                    <span>Scan with phone camera</span>
                    <span>New code in <b style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 500, color: "var(--color-ink)" }}>{secsLeft}s</b></span>
                  </Box>
                  <Box sx={{ height: 4, borderRadius: "2px", bgcolor: "#ece9f2", overflow: "hidden" }}>
                    <Box sx={{ height: "100%", bgcolor: "var(--color-primary)", transition: "width 1s linear", width: `${(secsLeft / windowSeconds) * 100}%` }} />
                  </Box>
                </Box>
              </Box>
            )}

            {tally && (
              <Box data-testid="checkin-tally" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, border: "1px solid var(--color-hairline-violet)", borderRadius: "10px", px: 2, py: 1.75 }}>
                <Box>
                  <Box sx={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 28, fontWeight: 700, lineHeight: 1 }}>
                    <span data-testid="checkin-tally-count">{tally.checkedIn}</span>{" "}
                    <Box component="small" sx={{ fontSize: 15, color: "var(--color-on-dark-muted)", fontWeight: 500 }}>of {tally.enrolled}</Box>
                  </Box>
                  <Typography sx={{ fontSize: 12, color: "var(--color-on-dark-muted)" }}>Students checked in</Typography>
                </Box>
              </Box>
            )}

            {code && (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Box component="code" data-testid="checkin-url" sx={{
                  flex: 1, minWidth: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: 12, bgcolor: "var(--color-surface-night)",
                  border: "1px solid var(--color-hairline-violet)", borderRadius: "8px", px: 1.5, height: 40, display: "flex", alignItems: "center",
                  overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", color: "var(--color-on-dark-muted)",
                }}>{url}</Box>
                <Button onClick={copyLink} aria-label="Copy check-in link" sx={darkBtn}>
                  <ContentCopyIcon sx={{ fontSize: 16 }} />{copied ? "Copied" : null}
                </Button>
              </Box>
            )}

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: "auto" }}>
              <Button onClick={fullScreen} disabled={!code} sx={darkBtn}><FullscreenIcon sx={{ fontSize: 16 }} />Full screen</Button>
              <Button onClick={showPrinted} disabled={!code} sx={darkBtn}><PrintOutlinedIcon sx={{ fontSize: 16 }} />Printable code</Button>
            </Box>
          </Box>

          <Box component="section" sx={{ display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
            <Box component="header" sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, px: 3.5, pt: 3, pb: 2.25, borderBottom: "1px solid var(--color-hairline-cloud)" }}>
              <Box>
                <Typography component="h3" sx={{ m: 0, fontSize: 18, fontWeight: 600 }}>Check-in settings</Typography>
                <Typography sx={{ color: "#7b7389", fontSize: 13 }}>Changes apply to this session immediately.</Typography>
              </Box>
              <IconButton aria-label="Close" onClick={onClose} sx={{ borderRadius: "8px" }}><CloseIcon /></IconButton>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 3.5, pt: 2.5, pb: 3, display: "grid", gap: 3, alignContent: "start" }}>
              <Box>
                <Typography component="h3" sx={{ fontSize: 11, fontWeight: 600, letterSpacing: ".12em", textTransform: "uppercase", color: "#7b7389", mb: 1.25 }}>
                  Room location
                </Typography>
                <Box sx={{ border: "1px solid var(--color-hairline-cloud)", borderRadius: "12px", p: 2, display: "grid", gridTemplateColumns: "auto minmax(0,1fr) auto", gap: 1.75, alignItems: "center" }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: "10px", display: "grid", placeItems: "center",
                    bgcolor: code?.locationSet ? "var(--color-success-bg)" : "var(--color-warn-bg)",
                    color: code?.locationSet ? "var(--color-success)" : "var(--color-warn)",
                  }}>
                    {code?.locationSet ? <PlaceIcon /> : <WrongLocationIcon />}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                      {code?.locationSet ? "Room location set" : "Room location isn't set"}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "#4a4257" }}>
                      {code?.locationSet
                        ? "Student check-ins are compared against it."
                        : "Students' locations can't be checked. Stand in the room and use your location."}
                    </Typography>
                  </Box>
                  <Button variant="outlined" color="inherit" size="small" onClick={useMyLocation} disabled={locating || !!error}
                    sx={{ textTransform: "uppercase", fontWeight: 600, borderColor: "var(--color-hairline-cool)", whiteSpace: "nowrap" }}>
                    {locating ? "Getting location…" : "Use my location"}
                  </Button>
                </Box>
                {locationNote && <Alert severity={locationNote.kind === "ok" ? "success" : "error"} sx={{ mt: 1.5 }}>{locationNote.text}</Alert>}
                {printError && <Alert severity="error" sx={{ mt: 1.5 }}>{printError}</Alert>}
              </Box>

              {settings && <CheckinSettingsPanel settings={settings} onSave={saveSettings} />}
              {code && <CheckinReviewPanel items={review} onResolve={resolve} busyId={busyId} error={reviewError} />}
            </Box>

            <Box component="footer" sx={{ borderTop: "1px solid var(--color-hairline-cloud)", px: 3.5, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5 }}>
              <Typography sx={{ fontSize: 12, color: "#7b7389" }}>Code stays active until the session ends.</Typography>
              <Button variant="contained" onClick={onClose} sx={{ textTransform: "uppercase", fontWeight: 600, bgcolor: "var(--color-primary)", "&:hover": { bgcolor: "#2a1f47" } }}>Done</Button>
            </Box>
          </Box>
        </Box>
      )}
    </Dialog>
  );
}
